// RFC-0008 T4: 模板元数据（前端展示用，与 skill 的 templates/ 目录对齐）
export type TemplateId =
  | 'consulting-mck'
  | 'qiji-bold'
  | 'report-annual'
  | 'proposal-clean'
  | 'product-launch'
  | 'edu-course'
  | 'status-weekly'
  | 'beipo-corp'
  | 'pitch-vc'
  | 'academic-research'
  | 'gov-formal'
  | 'finance-ir'
  | 'minimal-mono'
  | 'tech-dev-dark'
  | 'medical-health'
  | 'marketing-campaign'
  | 'esg-sustainability'
  | 'legal-formal';

export interface TemplateMeta {
  id: TemplateId;
  name: string;
  description: string;
  useCase: string;
  /** 模板典型 demo 主题，用于预览页 hero 文案 */
  demoTopic: string;
  accentColor: string; // 用于卡片视觉区分
  /** 完整色板描述，给 reference 文案用 */
  palette: string;
  /** 典型页型清单，让用户清楚模板覆盖什么场景 */
  pages: string;
  /** 模板基础页数（与 pages 清单条目数一致），用于 prefill 默认页数提示 */
  basePages: number;
  /** 整体调性 / 适合人群 */
  vibe: string;
}

export const TEMPLATES: readonly TemplateMeta[] = [
  {
    id: 'consulting-mck',
    name: '麦肯锡咨询风',
    description: '白底灰阶 + 单 accent 蓝，横线分隔',
    useCase: '战略 / 财报 / 行业分析 / 投资人尽调',
    demoTopic: 'AURORA Motors 全球新能源车战略分析',
    accentColor: '#005DAA',
    palette: '主色深蓝 #005DAA + 灰阶 + 白底，单 accent 不铺色块',
    pages: '封面 / 执行摘要 / 章节分隔 / KPI 数据 / 双栏分析 / 章节结尾 / 结论页',
    basePages: 7,
    vibe: '专业、克制、信息密度大、给高管和分析师看',
  },
  {
    id: 'qiji-bold',
    name: '奇绩创坛',
    description: '电子蓝大色块 + 奇绩官方 logo',
    useCase: '奇绩创坛路演日 / 投资人 Demo Day',
    demoTopic: 'BluePilot Marine 智能水务路演',
    accentColor: '#00A2FF',
    palette: '电子蓝 #00A2FF 大色块 + 红 accent #FF644E + 白底，奇绩官方 logo',
    pages: '封面 / 痛点 / 方案 / 市场 / 商业模式 / 团队 / 数据 / 联系',
    basePages: 8,
    vibe: '路演节奏感、视觉冲击大、3 分钟讲完一切、给 VC 和评委看',
  },
  {
    id: 'report-annual',
    name: '期刊年报',
    description: '米白纸 + 报刊红 + 深蓝，杂志感',
    useCase: '年报 / 白皮书 / 行业研究 / 数据故事',
    demoTopic: '2026 全球新能源车市场展望',
    accentColor: '#C8102E',
    palette: '米白纸 #FAF7F0 + 报刊红 #C8102E + 深蓝 #005AAA',
    pages: '封面 / 执行摘要 / 柱状图 / 数据表格 / 整页大字引文 / 多列展望 / 结尾',
    basePages: 7,
    vibe: '杂志感、双栏密集、含 FIG.NN/TABLE.NN 编号、给行业读者和决策者看',
  },
  {
    id: 'proposal-clean',
    name: '商务方案',
    description: '白底 + 单 accent 青绿，左对齐表格友好',
    useCase: '客户提案 / 项目方案 / 标书 / 报价',
    demoTopic: 'ACME Corp 品牌升级咨询提案',
    accentColor: '#0F766E',
    palette: '白底 + 青绿 accent #0F766E + 灰阶',
    pages: '封面 / 议程 / 项目背景 / 方案矩阵 / 时间线 / 团队 / 报价表 / 结尾',
    basePages: 8,
    vibe: '干净专业、表格友好、左对齐、给客户决策人看',
  },
  {
    id: 'product-launch',
    name: '发布会极简',
    description: '黑白交错 + 电子青 accent，每页一句话',
    useCase: '产品发布 / 技术宣讲 / Keynote',
    demoTopic: 'NovaCore X1 AI 推理加速器发布',
    accentColor: '#00D4FF',
    palette: '黑底 #0A0A0A 与白底交错 + 电子青 accent #00D4FF',
    pages: '封面 / 痛点 / 方案 / 大数字 / 引言金句 / CTA 收尾',
    basePages: 6,
    vibe: 'Stripe / Apple Keynote 极简、每页一句话或一个数字、给大屏发布会看',
  },
  {
    id: 'edu-course',
    name: '培训课件',
    description: '淡黄米底 + 暖黄 + 紫色，结构清晰',
    useCase: '内训 / 知识分享 / 教学课件 / 操作手册',
    demoTopic: 'LangGraph 多智能体工作流入门',
    accentColor: '#EAB308',
    palette: '淡黄米底 #FFFBEB + 暖黄 #EAB308 + 紫色 #7C3AED',
    pages: '封面 / 学习目标 / 核心概念 / 案例分析 / 思维导图 / 课堂练习 / 回顾 / 下节预告',
    basePages: 8,
    vibe: '暖色亲和、章节大编号、上课感、给学员看',
  },
  {
    id: 'status-weekly',
    name: '周报 KPI',
    description: '白底网格 + 三色状态卡 + 进度条',
    useCase: '周报 / 月报 / OKR review / 项目进度',
    demoTopic: 'Aurora 工程团队第 22 周周报',
    accentColor: '#2563EB',
    palette: '白底 + 蓝 #2563EB + 绿 #16A34A（达成）+ 红 #DC2626（风险）',
    pages: '封面 / 3 句话总结 / KPI 关键指标 / 项目进度条 / 风险与阻塞 / 下周动作项',
    basePages: 6,
    vibe: '3 秒读完、信息密集、用色彩做状态标识、给老板和团队看',
  },
  {
    id: 'beipo-corp',
    name: '北坡通用介绍',
    description: '雪山封面 + 半透蒙版 + 左 1/3 蓝色分章页',
    useCase: '公司通用介绍 / 业务汇报 / 投资人路演',
    demoTopic: '北坡 NorthApp 智能体平台介绍',
    accentColor: '#5F99FF',
    palette: '主蓝 #5F99FF + 高亮深蓝 #004DBE + 弱化灰 #A5A5A5 + 红色强调 #FF644E',
    pages: '封面 / 目录 / 章节综述 / 能力矩阵 / 产品架构 / 代码与性能 / 案例与效果数据 / 致谢',
    basePages: 8,
    vibe: '克制专业、论证密度高、保留北坡内部正式风、给投资人和客户看',
  },
  {
    id: 'pitch-vc',
    name: '融资路演 BP',
    description: '深墨蓝 + 暖金，标准 VC pitch 结构',
    useCase: '天使 / Pre-A / A 轮融资路演 BP',
    demoTopic: 'Cobalt 工业视觉质检平台 Pre-A 融资',
    accentColor: '#F5B301',
    palette: '深墨蓝 #0B1F3A + 暖金 #F5B301 + 灰阶',
    pages: '封面 / 问题 / 解法 / 产品 / 市场 TAM / 牵引力 / 商业模式 / 竞争 / 团队 / 融资额',
    basePages: 10,
    vibe: '商务有力、数字与曲线驱动、含 TAM/SAM/SOM 与单位经济、给投资人看',
  },
  {
    id: 'academic-research',
    name: '学术答辩',
    description: '牛津深蓝 + 勃艮第红，期刊严谨感',
    useCase: '开题 / 结题答辩 / 论文宣讲 / 学术会议',
    demoTopic: '基于小样本学习的工业缺陷检测方法研究',
    accentColor: '#8B1A1A',
    palette: '牛津深蓝 #002147 + 勃艮第红 #8B1A1A + 灰阶',
    pages: '封面 / 大纲 / 研究背景 / 研究问题 / 文献综述 / 方法 / 实验设计 / 结果 / 讨论 / 结论 / 致谢',
    basePages: 11,
    vibe: '严谨期刊感、图 N/表 N 题注、引用规范、给答辩委员和学者看',
  },
  {
    id: 'gov-formal',
    name: '政务汇报',
    description: '暖米白 + 党政红 + 金，中式庄重',
    useCase: '政府 / 党政 / 国企工作汇报 / 述职',
    demoTopic: 'XX 单位 2025 年度工作汇报',
    accentColor: '#C8102E',
    palette: '暖米白 #FBF9F4 + 党政红 #C8102E + 金 #C9A227',
    pages: '封面 / 提纲 / 工作回顾 / 主要成绩 / 重点工作 / 数据图表 / 典型案例 / 问题不足 / 工作计划 / 结尾',
    basePages: 10,
    vibe: '庄重稳重权威、中文序号、徽标占位、给上级和班子看',
  },
  {
    id: 'finance-ir',
    name: '业绩发布 IR',
    description: '炭黑 + 香槟金，涨绿跌红财务色',
    useCase: '上市公司业绩发布 / 季报年报 / IR 路演',
    demoTopic: '晟元科技 SUNYUAN 2025 年度业绩发布',
    accentColor: '#C99A3B',
    palette: '炭黑 #1C1C1E + 香槟金 #C99A3B + 涨绿 #16A34A / 跌红 #DC2626',
    pages: '封面 / 业绩亮点 / 收入分析 / 盈利能力 / 财务摘要表 / 分部业务 / 现金流 / 业绩指引 / 股东回报 / 结尾',
    basePages: 10,
    vibe: '专业冷静、财务表格图表密集、强调同比环比、给投资者和分析师看',
  },
  {
    id: 'minimal-mono',
    name: '极简黑白',
    description: '纯黑白 + 单朱红，瑞士国际主义排版',
    useCase: '主题演讲 / 品牌主张 / 观点输出 / 读书分享',
    demoTopic: '设计主张 · 极简编辑风',
    accentColor: '#E2231A',
    palette: '纯白 + 近黑 #111111 + 单朱红 #E2231A',
    pages: '封面 / 主张 / 目录 / 章节大字 / 观点 / 核心数字 / 要点列表 / 引言 / 对比 / 结尾',
    basePages: 10,
    vibe: '克制高级编辑感、极致留白、超大字号对比、朱红仅点一处、给设计审美人群看',
  },
  {
    id: 'tech-dev-dark',
    name: '技术分享',
    description: 'GitHub 暗色 + 终端绿，代码块与架构图',
    useCase: '技术大会 / 架构评审 / 开源宣讲 / 开发者分享',
    demoTopic: 'Edge Runtime v2.0 把推理跑到边缘',
    accentColor: '#3FB950',
    palette: 'GitHub 暗 #0D1117 + 终端绿 #3FB950 + 链接蓝 #58A6FF + 错误红 #F85149',
    pages: '封面 / 背景 / 架构图 / 代码示例 / 性能基准 / 技术选型 / 迁移落地 / 采用数据 / 路线图 / 结尾',
    basePages: 10,
    vibe: 'IDE 暗色 + 终端提示符 + 语法高亮、信息密度高、给工程师和架构师看',
  },
  {
    id: 'medical-health',
    name: '医疗健康',
    description: '临床白 + 医疗青绿 + 警示红',
    useCase: '科室汇报 / 临床研究 / 健康科普 / 患者教育',
    demoTopic: '2 型糖尿病基层诊疗路径',
    accentColor: '#14B8A6',
    palette: '临床白 + 医疗墨绿 #0E4A47 + 青绿 #14B8A6 + 警示红 #E11D48',
    pages: '封面 / 大纲 / 流行病学 / 发病机制 / 诊断标准 / 治疗方案 / 疗效数据 / 风险提示 / 诊疗建议 / 结尾',
    basePages: 10,
    vibe: '洁净专业可信、临床数据正常青绿异常红、含医学免责注、给医师和患者看',
  },
  {
    id: 'marketing-campaign',
    name: '营销活动',
    description: '高饱和珊瑚渐变 + 活力多彩，增长可视化',
    useCase: '营销提案 / 品牌 campaign / 增长复盘',
    demoTopic: 'NOVA 焕新 · 夏日增长 campaign',
    accentColor: '#FF5A5F',
    palette: '珊瑚红 #FF5A5F→橙 #FF8A5B 渐变 + 深紫黑 #1A1530 + 紫 #6C5CE7 + 薄荷 #00C2A8',
    pages: '封面 / 活动目标 / 目标人群 / 策略主线 / 转化漏斗 / 渠道矩阵 / 活动排期 / 增长成果 / ROI 总结 / CTA 收尾',
    basePages: 10,
    vibe: '年轻热血有冲击、渐变满版 + 漏斗 + 增长曲线、活泼但有序、给市场和增长团队看',
  },
  {
    id: 'esg-sustainability',
    name: 'ESG 可持续',
    description: '森林绿大地色系，E/S/G 三色体系',
    useCase: 'ESG 报告 / 可持续发展 / 碳中和 / 公益 CSR',
    demoTopic: '青绿集团 2025 可持续发展报告',
    accentColor: '#2F9E68',
    palette: '森林绿 #14532D + 生机绿 #2F9E68 + 天蓝 #3B82C4 + 暖砂金 #D6A045',
    pages: '封面 / 致辞 / ESG 总览 / 环境 E / 气候行动 / 社会 S / 公益项目 / 治理 G / SDGs 对标 / 路线图 / 结尾',
    basePages: 11,
    vibe: '自然可信有责任感、E/S/G 三色 + 影响力大数字 + SDGs 对标、给监管和公众看',
  },
  {
    id: 'legal-formal',
    name: '法律意见',
    description: '深藏蓝 + 金，条款编号体系',
    useCase: '法律意见书 / 合规 / 律所尽调 / 风险评估',
    demoTopic: 'XX 并购项目法律尽职调查报告',
    accentColor: '#B08D4F',
    palette: '深藏蓝 #0A1A33 + 律所金 #B08D4F + 暗红 #9B2C2C + 象牙白',
    pages: '封面 / 目录 / 交易背景 / 交易结构 / 尽调发现 / 合规要点 / 风险评估 / 法律意见 / 建议措施 / 结尾',
    basePages: 10,
    vibe: '极度严谨保守权威、条款编号 + 风险等级表 + 交易架构图、含法律免责注、给客户和决策人看',
  },
] as const;

export function getTemplateById(id: string): TemplateMeta | undefined {
  return TEMPLATES.find((t) => t.id === id);
}

export function getTemplateName(id: string): string {
  return getTemplateById(id)?.name ?? id;
}

import type { ChatInputReference } from './north-sdk';

const SKILL_NAME = '小北PPT技能';
const SKILL_DESC =
  '用手写 SVG → 原生可编辑 PPTX 的范式制作幻灯片（18 套模板，native shape 点哪改哪）。配合 decks 应用使用，产物上传到 decks。';
const APP_ID = 'decks';
const APP_NAME = 'PPT';

/**
 * 构造「指定模板」场景的引用芯片：
 * - skill 引用：告诉 agent 必须用 svg-pptx-deck skill（skill_id 用运行时真实 id）
 * - app_custom 引用：携带当前 app 信息 + ppt_user_id + 选定模板的风格元数据（agent 可读）
 * skillId 由调用方在运行时从 host（isArtifactInstalled/installArtifact）拿到，避免跨环境写死。
 */
export function buildTemplateReferences(
  template: TemplateMeta,
  appUserId: string,
  skillId: string | null,
): ChatInputReference[] {
  const refs: ChatInputReference[] = [];
  if (skillId) {
    refs.push({
      name: SKILL_NAME,
      type: 'skill',
      skill_id: skillId,
      description: SKILL_DESC,
    });
  }
  refs.push({
    name: APP_NAME,
    type: 'app_custom',
    app_id: APP_ID,
    data: {
      ppt_user_id: appUserId,
      // 始终告知 agent 用哪个 skill（不依赖 skill 芯片是否注入成功）
      skill_name: SKILL_NAME,
      skill_instruction: `本任务必须使用 skill「${SKILL_NAME}」制作 PPT：${SKILL_DESC}`,
      template_id: template.id,
      template_name: template.name,
      palette: template.palette,
      pages: template.pages,
      vibe: template.vibe,
      use_case: template.useCase,
      base_pages: template.basePages,
    },
  });
  return refs;
}

/**
 * 构造「空白新建」场景的引用芯片（不指定模板，让 agent 按主题自选）。
 */
export function buildBlankReferences(
  appUserId: string,
  skillId: string | null,
): ChatInputReference[] {
  const refs: ChatInputReference[] = [];
  if (skillId) {
    refs.push({
      name: SKILL_NAME,
      type: 'skill',
      skill_id: skillId,
      description: SKILL_DESC,
    });
  }
  refs.push({
    name: APP_NAME,
    type: 'app_custom',
    app_id: APP_ID,
    data: {
      ppt_user_id: appUserId,
      // 始终告知 agent 用哪个 skill（不依赖 skill 芯片是否注入成功）
      skill_name: SKILL_NAME,
      skill_instruction: `本任务必须使用 skill「${SKILL_NAME}」制作 PPT：${SKILL_DESC}`,
      available_templates: TEMPLATES.map((t) => ({
        id: t.id,
        name: t.name,
        use_case: t.useCase,
      })),
    },
  });
  return refs;
}

/**
 * 把引用数组序列化成 host 端 render-text-with-mentions 能解析的
 * <reference>[...]</reference> 文本。字段对齐后端 BackendReferenceItem：
 *   - skill        → { name, type:'skill', skill_id, description }
 *   - app_custom   → { name, type:'app_custom', app_id, data }
 * 发送后这段会被宿主渲染成 mention 芯片，agent 也能解析到 skill/app 元数据。
 */
export function serializeReferences(refs: ChatInputReference[]): string {
  if (refs.length === 0) return '';
  const backend = refs.map((r) => {
    if (r.type === 'skill') {
      return {
        name: r.name,
        type: 'skill',
        skill_id: r.skill_id,
        description: r.description,
      };
    }
    // app_custom
    return {
      name: r.name,
      type: 'app_custom',
      app_id: r.app_id,
      ...(r.data ? { data: r.data } : {}),
    };
  });
  return `<reference>${JSON.stringify(backend)}</reference>`;
}

/**
 * 组合「指定模板」场景写进输入框的完整文本：
 * reference（skill + app_custom，发送后成芯片）+ 换行 + 可编辑填空模板。
 */
export function buildTemplateChatInput(
  template: TemplateMeta,
  appUserId: string,
  skillId: string | null,
): string {
  const refs = buildTemplateReferences(template, appUserId, skillId);
  const refText = serializeReferences(refs);
  const prefill = buildTemplatePrefill(template);
  return refText ? `${refText}\n${prefill}` : prefill;
}

/**
 * 组合「空白新建」场景写进输入框的完整文本。
 */
export function buildBlankChatInput(
  appUserId: string,
  skillId: string | null,
): string {
  const refs = buildBlankReferences(appUserId, skillId);
  const refText = serializeReferences(refs);
  const prefill = buildBlankPrefill();
  return refText ? `${refText}\n${prefill}` : prefill;
}

/**
 * 构造写到聊天输入框的明文引导（用户可见、可编辑、需用户补充后发送）。
 */
export function buildTemplatePrefill(template: TemplateMeta): string {
  return [
    `用「${template.name}」风格做一份 PPT，请按下面信息生成：`,
    '',
    '主题 / 业务：',
    '目标听众：',
    '核心要点（3-5 条）：',
    '- ',
    '- ',
    '- ',
    `期望页数：${template.basePages} 页左右`,
    '其他要求（颜色 / 数据 / logo 等）：',
  ].join('\n');
}

/**
 * 空白新建场景下的输入框引导明文。
 */
export function buildBlankPrefill(): string {
  return [
    '帮我做一份 PPT，请按下面信息生成：',
    '',
    '主题 / 业务：',
    '目标听众：',
    '核心要点（3-5 条）：',
    '- ',
    '- ',
    '- ',
    '期望风格（可选模板：麦肯锡咨询 / 奇绩创坛 / 期刊年报 / 商务方案 / 发布会极简 / 培训课件 / 周报 KPI / 北坡通用 / 融资路演 / 学术答辩 / 政务汇报 / 业绩 IR / 极简黑白 / 技术分享 / 医疗健康 / 营销活动 / ESG 可持续 / 法律意见）：',
    '期望页数：',
  ].join('\n');
}
